import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Phone, CheckCircle, MapPin } from "@phosphor-icons/react";
import { LOGO_FULL, PHONE_DISPLAY, PHONE_TEL, SERVICE_AREA } from "@/lib/brand";

export default function Hero() {
  const bullets = [
    "Home & office cleaning, inside and out",
    "Handyman repairs, installations, and new construction help",
    "Flexible scheduling: one-time, weekly, bi-weekly, or monthly",
  ];

  return (
    <section className="relative overflow-hidden bg-stone-50" data-testid="hero-section">
      {/* soft background accents */}
      <div className="absolute inset-0 bg-soft-grid opacity-60 pointer-events-none" />
      <div className="absolute -top-40 -right-40 w-[32rem] h-[32rem] rounded-full bg-sky-200/30 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[28rem] h-[28rem] rounded-full bg-sky-100/50 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-sky-700 bg-white border border-sky-100 px-3 py-1.5 rounded-full shadow-sm">
              <MapPin size={14} weight="bold" /> {SERVICE_AREA}
            </div>

            <h1 className="mt-6 font-display font-extrabold text-slate-900 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Reliable home, office &amp; handyman services —{" "}
              <span className="text-sky-500">all from one trusted team.</span>
            </h1>

            <p className="mt-6 text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl">
              Heavenly Home And Office handles interior and exterior cleaning, organization,
              handyman work, yard care, and tech setup. One number, one neighborly crew, common
              sense fixes that actually stick.
            </p>

            <ul className="mt-8 space-y-3" data-testid="hero-bullets">
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-3 text-slate-700">
                  <CheckCircle size={22} weight="fill" className="text-sky-500 mt-0.5 shrink-0" />
                  <span className="text-base md:text-lg">{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <a
                href="#quote"
                className="inline-flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 text-white font-semibold text-base px-8 py-4 rounded-full transition-all shadow-lg shadow-sky-500/30 hover:shadow-xl hover:-translate-y-0.5"
                data-testid="hero-quote-button"
              >
                Request a Free Quote
                <ArrowRight size={18} weight="bold" />
              </a>
              <a
                href={`tel:${PHONE_TEL}`}
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-50 text-slate-900 font-semibold text-base px-8 py-4 rounded-full border border-slate-200 transition-all hover:-translate-y-0.5"
                data-testid="hero-call-button"
              >
                <Phone size={18} weight="bold" />
                Call {PHONE_DISPLAY}
              </a>
            </div>

            <div className="mt-6 text-sm text-slate-500">
              Prefer to text or email? We answer both — see the footer for details.
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative rounded-[2rem] overflow-hidden shadow-2xl shadow-sky-900/10 rotate-[1.5deg] bg-gradient-to-br from-sky-50 via-white to-sky-100">
              <div className="relative flex items-center justify-center w-full h-[420px] md:h-[520px] p-8 md:p-12">
                <img
                  src={LOGO_FULL}
                  alt="Heavenly Home And Office"
                  className="w-full h-full object-contain drop-shadow-xl"
                />
              </div>
            </div>

            {/* trust card */}
            <div className="absolute -bottom-6 -right-4 sm:-right-6 bg-white rounded-2xl px-5 py-4 shadow-xl border border-slate-100 max-w-[240px]">
              <div className="flex items-center gap-2 text-sky-600 text-xs font-semibold uppercase tracking-widest">
                <CheckCircle size={14} weight="fill" /> Trusted Locally
              </div>
              <div className="mt-1 text-sm text-slate-700 leading-snug">
                Chattanooga, Hamilton, Bradley, Catoosa &amp; Meigs counties.
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
